import type { MeResponse } from "@seeft/shared";
import { computed, ref } from "vue";
import { defineStore } from "pinia";

import { apiGet, apiPost } from "../api/client";
import { clearStoredDevToken, getStoredDevToken, setStoredDevToken } from "../lib/devToken";

export const useSessionStore = defineStore("session", () => {
  const state = ref<"loading" | "authenticated" | "signed-out" | "auth-error">(
    "signed-out"
  );
  const authError = ref<string | null>(null);
  const profile = ref<MeResponse | null>(null);
  const userName = ref("Guest");
  const planName = ref("Signed out");
  const savedCount = ref(0);
  const isDevMode = ref(false);
  const authProvider = ref<"none" | "dev-token" | "token" | "google" | "microsoft">("none");

  const statusLabel = computed(() =>
    isDevMode.value
      ? "DEV TOKEN MODE"
      : state.value === "authenticated"
        ? "LIVE ACCOUNT"
        : "SIGNED OUT"
  );

  const isAuthenticated = computed(() => state.value === "authenticated");

  async function bootstrap() {
    state.value = "loading";
    authError.value = null;

    const result = await apiGet<MeResponse>("/me");

    if (result.error) {
      profile.value = null;
      userName.value = "Guest";
      planName.value = "Signed out";
      savedCount.value = 0;
      isDevMode.value = false;
      authProvider.value = "none";

      if (result.error.code === "UNAUTHORIZED") {
        // Clear any stale dev token from localStorage
        clearStoredDevToken();
        state.value = "signed-out";
        return;
      }

      authError.value = result.error.message;
      state.value = "auth-error";
      return;
    }

    profile.value = result.data;
    userName.value = result.data.name?.trim() || result.data.email || "Seeft user";
    planName.value = result.data.subscription_plan || result.data.effective_tier;
    savedCount.value = result.data.unreadNotificationCount ?? 0;
    // Check if this is a dev user (identified by email or by having a dev token stored)
    const hasDevToken = getStoredDevToken() !== null;
    isDevMode.value = result.data.email === "dev@seeft.local" || hasDevToken;
    // Determine auth provider based on user data
    if (result.data.email === "dev@seeft.local" || hasDevToken) {
      authProvider.value = "dev-token";
    } else if (result.data.email?.endsWith("@seeft.local")) {
      authProvider.value = "token";
    } else {
      authProvider.value = "google";
    }
    state.value = "authenticated";
  }

  async function signOut() {
    clearStoredDevToken();
    await apiPost("/auth/logout", {});
    await bootstrap();
  }

  // Exchange dev token for session cookie
  async function signInWithDevToken(token: string): Promise<boolean> {
    const result = await apiPost("/auth/dev", {}, { headers: { "X-Dev-Token": token } });
    
    if (result.error) {
      authError.value = result.error.message;
      return false;
    }

    // Store dev token for future reference (admin operations)
    setStoredDevToken(token);
    
    // Bootstrap to get user profile
    await bootstrap();
    return state.value === "authenticated";
  }

  // Exchange issued token for session cookie
  async function signInWithToken(token: string): Promise<boolean> {
    const result = await apiPost("/auth/token", {}, { headers: { "Authorization": `Bearer ${token}` } });
    
    if (result.error) {
      authError.value = result.error.message;
      return false;
    }

    // Bootstrap to get user profile
    await bootstrap();
    return state.value === "authenticated";
  }

  return {
    authError,
    authProvider,
    bootstrap,
    isAuthenticated,
    isDevMode,
    planName,
    profile,
    savedCount,
    signInWithDevToken,
    signInWithToken,
    signOut,
    state,
    statusLabel,
    userName
  };
});
